import { DocumentInfo } from './document'
import { FundingInstructionSummaryInfo } from './fundingInstructions'
import { paymentFeesInfo } from './invoice'

// --------- Info ---------
export type FuelAdvanceInfo = {
  id: string
  invoiceAmount: number
  fuelAdvanceAmount: number
  balance: number
  invoiceNumber: string
  referenceNumber: string
  companyClientId: string
  companyClientName: string
  companyDebtorId: string
  companyDebtorName: string
  fuelAdvanceFeeAmount: number
  fuelAdvanceFeePercentage: number
  status: string
  createdOn: string
  updatedOn: string
  notes: string
  documents: DocumentInfo[]
  deliveryDate?: string
  type: FuelAdvanceType
  issueDate?: string
  closedDate?: string
}

export type FundingChargebackStatus = 'PENDING' | 'IN_QUEUE' | 'COMPLETED'

export type ActiveFundingChargebackInfo = {
  amount: number
  status: FundingChargebackStatus
  batchId: string
  batchCode: string
}

export type TotalFuelAdvanceInfo = {
  totalPendingAmount: number
  totalApprovedAmount: number
  totalRejectedAmount: number
  totalClosedAmount: number
  totalOpenAmount: number
  totalAllFuelAdvancesAmount: number
  countPendingFuelAdvances: number
  countApprovedFuelAdvances: number
  countRejectedFuelAdvances: number
  countClosedFuelAdvances: number
  countOpenFuelAdvances: number
  countAllFuelAdvances: number
}

// --------- Response ---------

export type FuelAdvanceSingletonResponse = {
  fuelAdvance: FuelAdvanceInfo
}

export type FuelAdvanceCollectionResponse = {
  fuelAdvances: FuelAdvanceInfo[]
}

export type ActiveFundingChargebackResponse = {
  activeFundingChargeback: ActiveFundingChargebackInfo | null
}

export type TotalFuelAdvanceResponse = {
  totalFuelAdvanceInfo: TotalFuelAdvanceInfo
}

export type FuelAdvanceCollectionTotalsInfo = {
  count: number
  totalAdvanceAmount: number
  totalFeeAmount: number
}

export type FuelAdvanceCollectionTotalsResponse = {
  totals: FuelAdvanceCollectionTotalsInfo
}

export type ApproveForFundingFuelAdvancesSummaryResponse = {
  totalFuelAdvanceAmounts: number
  totalFees: number
  totalNetAmount: number
  paymentFees: paymentFeesInfo
  fundingInstruction: FundingInstructionSummaryInfo[]
}

export type AssignChargebackToFuelAdvanceResponse = {
  fuelAdvances: FuelAdvanceInfo[]
}

// --------- Params ---------
export type FuelAdvanceGetCollectionParams = {
  query?: string
  status?: string
  client?: string
  debtor?: string
  sort?: string
  invoiceGroupId?: string
  invoiceLinked?: boolean
  accountExecutiveUserId?: string
  companyTeamId?: string
  type?: FuelAdvanceType | null
}

export type TotalFuelAdvanceParams = {
  client?: string | null
  debtor?: string | null
}

export type FuelAdvanceApproveForFundingSummaryParams = {
  fuelAdvances?: string
  selectAllRequest?: boolean
  companyClientId: string
}

// --------- Request ---------
export type CreateFuelAdvanceRequest = {
  invoiceAmount?: number | null
  fuelAdvanceAmount?: number | null
  invoiceNumber?: string | null
  referenceNumber?: string
  companyClientId?: string | null
  companyDebtorId?: string | null
  fuelAdvanceFeeAmount?: number | string | null
  fuelAdvanceFeePercentage?: number | string | null
  notes?: string
  deliveryDate?: string
  type?: FuelAdvanceType | null
  issueDate?: string
}

export type UpdateFuelAdvanceRequest = {
  documents?: string[]
  invoiceAmount?: number | null
  fuelAdvanceAmount?: number | null
  invoiceNumber?: string | null
  referenceNumber?: string | null
  companyClientId?: string | null
  companyDebtorId?: string | null
  fuelAdvanceFeeAmount?: number | null | string
  fuelAdvanceFeePercentage?: number | string | null
  notes?: string | null
  status?: string | null
  deliveryDate?: string | null
  type?: FuelAdvanceType | null
  issueDate?: string | null
}

export type ApproveForFundingFuelAdvancesRequest = {
  companyClientId?: string
  fundingInstructionId?: string
  zeroOutPaymentFee: boolean
  fuelAdvanceIds?: string[]
  selectAllRequest?: boolean
  fuelAdvanceChargebackIds?: string[]
  account?: string
}

export type AutocompleteFuelAdvanceMatchInvoiceParams = {
  companyClientId?: string
  companyDebtorId?: string
  invoiceNumber?: string
  referenceNumber?: string
  invoiceAmount?: number
}

export type FuelAdvanceChargebackItem = {
  fuelAdvanceId: string
  amount: number
}

export type AssignChargebackToFuelAdvanceRequest = {
  items: FuelAdvanceChargebackItem[]
}

export type RejectFuelAdvanceRequest = {
  companyClientId: string
  fuelAdvanceIds?: string[]
  selectAllRequest?: boolean
}

export type RemoveFuelAdvancesFromInvoiceGroupRequest = {
  fuelAdvanceIds?: string[] | null
  status?: 'REJECTED' | 'PENDING' | null
}

// ============= ENUMS =============
export enum FuelAdvanceType {
  INVOICE = 'INVOICE',
  CREDIT = 'CREDIT',
}
